import { Badge, Checkbox, Flex, List, ListItem, Stack, Text } from '@chakra-ui/react';
import { titleCase } from 'title-case';
import {
  Filter,
  FilterType,
  getCoverageBadgeText,
  getCoverageBadgeColor,
  shouldRenderBadge,
} from '@components/wizard/shared/Recommendations/utils';
import Body2 from '@components/typography/Body2';

type Props = {
  title?: string;
  type: FilterType;
  filters: [Filter];
  onFilterChange: (type: string, id: string, checked: boolean) => void;
  providerData?: any[];
};

export const renderName = (filter) => {
  if (filter.type === FilterType.prescriptions) {
    return titleCase(filter.name.toLowerCase());
  }
  return filter.name;
};

const FilterList = ({ type, title, filters, onFilterChange, providerData = null }: Props) => {
  const isProvider = type === FilterType.primaryProviders || type === FilterType.specialistProviders;

  if (!filters.length || (isProvider && !providerData)) {
    return null;
  }

  const sorted = filters.sort((a, b) => a.name.localeCompare(b.name));

  const isNotCoveredPscrip = (filter) => filter.type === FilterType.prescriptions && filter.inNetworkPercentage === 0;

  const renderItem = (filter) => {
    let providerAddress = null;
    if (isProvider && providerData) {
      const data = providerData.find((provider) => provider.id === filter.value);
      if (data) {
        providerAddress = data.address_1;
      }
    }

    return (
      <ListItem
        key={filter.value}
        display="flex"
        flexDirection={['column', 'column', 'column', 'row']}
        alignItems={['start', 'start', 'start', 'center']}
        overflow="hidden"
        textOverflow="ellipsis"
        mb={2}
        sx={{ whiteSpace: 'nowrap' }}
      >
        <Stack spacing={1} w="100%">
          <Flex alignItems="center">
            <Checkbox
              isChecked={filter.checked}
              value={filter.value}
              colorScheme="primary"
              onChange={(event) => {
                const { checked } = event.target;
                onFilterChange(type, filter.value, checked);
              }}
              display={['block', 'flex']}
              alignItems="center"
              marginRight={1}
              maxWidth="100%"
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                '> *': {
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                },
              }}
              isDisabled={isNotCoveredPscrip(filter)}
            >
              {renderName(filter)}
            </Checkbox>
            {shouldRenderBadge(filter) && (
              <Badge ml="12px" colorScheme={getCoverageBadgeColor(filter)}>
                {getCoverageBadgeText(filter)}
              </Badge>
            )}
          </Flex>
          {providerAddress && (
            <Text fontSize={15} color="gray.500" pl="24px">
              {titleCase(providerAddress.toLowerCase())}
            </Text>
          )}
        </Stack>
      </ListItem>
    );
  };

  return (
    <Stack spacing={1}>
      {title ? (
        <Body2 color="gray.500" bold>
          {title}
        </Body2>
      ) : null}
      <List>{sorted.map((filter) => renderItem(filter))}</List>
    </Stack>
  );
};

export default FilterList;
